// If you're adding to this file strongly consider deleting it instead and pulling in a date library.

import { OfferingHit } from '@src/queries/user';
import { format, parse } from 'date-fns';

const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses an ISO date string and returns a Date object or null if the string cannot be parsed
 * @param dateString An ISO-8601 formatted date
 */
export function parseISODate(dateString: string): Date | null {
	if (typeof dateString !== 'string' || !dateRegex.test(dateString)) {
		return null;
	}

	const [year, month, day] = dateString.split('-').map(d => parseInt(d));
	return new Date(Date.UTC(year, month - 1, day));
}

/**
 * Format a date object or string in the current locale. If an unparsable string is passed it is returned unmodified.
 * @param date An instance of Date or an ISO formatted date string, see parseISODate
 * @param options Date formatting options
 */
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
	const dateObj = date instanceof Date ? date : parseISODate(date);
	if (!dateObj) {
		// If we were given a string and couldn't parse it, return that.
		return typeof date === 'string' ? date : '';
	}

	// The locale argument is undefined so it uses the browsers current locale
	// DO NOT CHANGE THIS undefined TO 'default', it will break IE 11
	// https://stackoverflow.com/questions/57269700/locale-default-is-not-well-formed
	return new Intl.DateTimeFormat(undefined, options).format(dateObj);
}

const referenceDate = new Date();
referenceDate.setHours(0);
referenceDate.setMinutes(0);
referenceDate.setSeconds(0);
referenceDate.setMilliseconds(0);

/**
 *
 * @param algoliaDates these will be in the format of "MMM DD, YYYY"
 * @returns the same date values in the format of "YYYY-MM-DD"
 */
export const convertAlgoliaDatesToCSDates = (
	algoliaDates: OfferingHit['nextDates']
): OfferingHit['nextDates'] => {
	const algoliaDateFormat = 'MMM dd, yyyy';
	const catalogServiceDateFormat = 'yyyy-MM-dd';

	// if dates were null coming in, send them out as null as well
	if (!algoliaDates?.appDueDate || !algoliaDates?.startDate) {
		return {
			appDueDate: null,
			startDate: null,
		};
	}

	const parsedAlgoliaAppDueDate = parse(algoliaDates.appDueDate, algoliaDateFormat, referenceDate);
	const parsedAlgoliaStartDate = parse(algoliaDates.startDate, algoliaDateFormat, referenceDate);

	const formattedAppDueDate = format(parsedAlgoliaAppDueDate, catalogServiceDateFormat);
	const formattedStartDate = format(parsedAlgoliaStartDate, catalogServiceDateFormat);
	return {
		appDueDate: formattedAppDueDate,
		startDate: formattedStartDate,
	};
};
